import type { Drop, DropPhase } from "@anaconda/core/drop/types"
import type { CollectionId } from "@anaconda/core/nft-collection/types"
import { Trans } from "@lingui/react/macro"
import { cn } from "@rarible_int/anaconda_ui_components"
import Link from "next/link"
import { useEffect, useState } from "react"
import { ArrowIcon } from "../icons/arrow"
import { getDropHref } from "../navigation/drop"

interface MintingNowProps {
  collectionId: CollectionId
  drop: Drop
  items?: number
  className?: string
  withLink?: boolean
}

/**
 * Compute current phase without using Reatom atoms to avoid activating drop effects.
 * This is a lightweight alternative for display-only components.
 */
function getCurrentPhase(phases: DropPhase[], now: number) {
  return phases.find((phase: DropPhase) => {
    const startDate = new Date(phase.startDate).getTime()
    const endDate = phase.endDate
      ? new Date(phase.endDate).getTime()
      : undefined

    return startDate <= now && (!endDate || now < endDate)
  })
}

export function MintingNow({
  collectionId,
  drop,
  items,
  className,
  withLink = true,
}: MintingNowProps) {
  const href = getDropHref(collectionId)
  const [isClient, setIsClient] = useState(false)

  // Only compute phase on client to avoid hydration mismatch
  useEffect(() => {
    setIsClient(true)
  }, [])

  if (!isClient) {
    return null
  }

  // Compute current phase directly without activating drop molecule effects
  const now = Date.now()
  const currentPhase = getCurrentPhase(drop.phases, now) || drop.phases[0]

  if (!currentPhase) {
    return null
  }

  const isMinting = checkIsMinting(
    currentPhase.startDate,
    currentPhase.endDate,
    drop.totalSupply,
    items,
  )

  if (!isMinting) {
    return null
  }

  if (!withLink) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-x-1 whitespace-nowrap rounded-xs bg-brand/10 px-1 py-0.5 font-bold text-3xs text-brand uppercase",
          className,
        )}
      >
        <Trans>Minting Now</Trans>
      </span>
    )
  }

  return (
    <Link
      href={href}
      className={cn(
        "inline-flex items-center gap-x-1 whitespace-nowrap rounded-xs bg-brand/10 px-1 py-0.5 font-bold text-3xs text-brand uppercase",
        className,
      )}
    >
      <Trans>Minting Now</Trans>
      <ArrowIcon className="-rotate-45 h-2 w-2" />
    </Link>
  )
}

function checkIsMinting(
  startDate: string,
  endDate?: string,
  totalSupply?: number,
  items?: number,
): boolean {
  const now = new Date().getTime()
  const start = new Date(startDate).getTime()

  if (now < start) {
    return false
  }

  if (endDate) {
    const end = new Date(endDate).getTime()
    if (now > end) {
      return false
    }
  }

  if (totalSupply !== undefined && items !== undefined) {
    if (totalSupply <= items) {
      return false
    }
  }

  return true
}
